import PropTypes from 'prop-types';
import { breakpoints, Inline, Stack, Text, Tooltip } from '@etg/wings';
import { Icon, InformationIconButton } from '@eti/components';
import { useTranslation } from '@eti/providers';
import { css } from '@eti/styles';
import { constants } from '@eti/utils';
import { CartRow } from './CartComponents';

const { MOUSE_FLOW_CLASS_NAMES } = constants;

const baggageHeadingStyles = css`
  align-items: center;
  display: flex;
  margin: 12px 0 6px;

  span {
    display: flex;
  }
`;

const infoIconTriggerStyles = css`
  cursor: pointer;

  @media (min-width: ${breakpoints._768}) {
    cursor: default;
  }
`;

const iconWrapperStyles = css`
  height: 1rem;
  margin-right: 8px;
  width: 1rem;

  [dir='rtl'] & {
    margin-left: 8px;
    margin-right: 0;
  }
`;

const BaggageHeading = ({ children, dataTestId, icon }) => {
  return (
    <Text className={baggageHeadingStyles} data-testid={dataTestId} variant="16-bold">
      <div className={iconWrapperStyles}>
        <Icon id={icon} size="20" />
      </div>
      {children}
    </Text>
  );
};
const checkedBaggageCartSection = css`
  width: 100%;
`;

const checkedBaggagedetailsPerTraveler = css`
  display: flex;
  justify-content: space-between;
  width: 100%;
`;

const secondBagdetailsPerTraveler = css`
  display: flex;
  justify-content: space-between;
  padding-inline-start: 8px;
  width: 100%;
`;

BaggageHeading.propTypes = {
  children: PropTypes.node,
  dataTestId: PropTypes.string,
  icon: PropTypes.string,
};

const CartBaggageSection = ({
  cabinBaggageSelection: tdCabinBaggageSelection,
  checkedBaggageSelection: tdCheckedBaggageSelection,
  dataTestId,
  personalItem: tdPersonalItem,
  bundleBaggageDetails: tdBundleBaggageDetails,
  bundleCabinBaggageDetails: tdBundleCabinBaggageDetails,
  isCabinBaggagePresent: tdIsCabinBaggagePresent,
  isCheckedBaggagePresent: tdIsCheckedBaggagePresent,
}) => {
  const { t } = useTranslation();

  const cabinBaggageSelection = tdCabinBaggageSelection;
  const checkedBaggageSelection = tdCheckedBaggageSelection;
  const personalItem = tdPersonalItem;
  const bundleBaggageDetails = tdBundleBaggageDetails;
  const bundleCabinBaggageDetails = tdBundleCabinBaggageDetails;
  const isCabinBaggagePresent = tdIsCabinBaggagePresent;
  const isCheckedBaggagePresent = tdIsCheckedBaggagePresent;

  const isPersonalItemIncluded =
    personalItem && !personalItem.some((obj) => obj.label === t('Cart.PersonalItem.NotIncluded'));
  const cabinBaggageHeadingIcon = isCabinBaggagePresent ? 'cabin-baggage' : 'no-cabin-baggage';
  const checkedBaggageHeadingIcon = isCheckedBaggagePresent
    ? 'check-in-baggage'
    : 'no-check-in-baggage';

  const getBaggageDetailsTranslation = (baggageDetails) => {
    return baggageDetails?.pieces
      ? t(
          'Cart.Baggage.IncludedForAll.Pieces',
          baggageDetails.pieces,
          baggageDetails.weight,
          baggageDetails.weightUnit,
        )
      : t(
          'Cart.Baggage.IncludedForAll.Weight',
          bundleBaggageDetails.weight,
          bundleBaggageDetails.weightUnit,
        );
  };
  return (
    <>
      <Text as="p" data-testid={`${dataTestId}-baggageSection-title`} variant="18-emphasized">
        {t('Cart.Baggage.Title')}
      </Text>
      {personalItem && (
        <>
          <BaggageHeading
            dataTestId={`${dataTestId}-baggageSection-personalItemTitle`}
            icon={isPersonalItemIncluded ? 'personal-item' : 'no-personal-item'}
          >
            <span>{t('Cart.PersonalItem.Title')}</span>
          </BaggageHeading>
          {personalItem.map((row) => (
            <CartRow
              className={MOUSE_FLOW_CLASS_NAMES}
              data-testid={`${dataTestId}-baggageSection-personalItemRow`}
              key={row.label}
            >
              <Text>{row.label}</Text>
              {row.value && <Text> {row.value}</Text>}
            </CartRow>
          ))}
        </>
      )}
      {bundleCabinBaggageDetails ? (
        <>
          <BaggageHeading
            dataTestId={`${dataTestId}-baggageSection-cabinBaggageTitle`}
            icon={cabinBaggageHeadingIcon}
          >
            <span>
              {t('cabinBaggagePerTravelerTitle') ||
                t('cabinBaggageViHeaderTitle') ||
                t('cabinBaggageTitle')}
            </span>
          </BaggageHeading>
          <CartRow
            className={MOUSE_FLOW_CLASS_NAMES}
            data-testid={`${dataTestId}-baggageSection-cabinBaggageRow`}
          >
            <Text as="bdi"> {getBaggageDetailsTranslation(bundleCabinBaggageDetails)}</Text>
          </CartRow>
        </>
      ) : (
        cabinBaggageSelection && (
          <>
            <BaggageHeading
              dataTestId={`${dataTestId}-baggageSection-cabinBaggageTitle`}
              icon={cabinBaggageHeadingIcon}
            >
              <span>
                {t('cabinBaggagePerTravelerTitle') ||
                  t('cabinBaggageViHeaderTitle') ||
                  t('cabinBaggageTitle')}
              </span>
            </BaggageHeading>
            {cabinBaggageSelection.map((row) => (
              <CartRow
                className={MOUSE_FLOW_CLASS_NAMES}
                data-testid={`${dataTestId}-baggageSection-cabinBaggageRow`}
                key={row.label}
              >
                <Text as="bdi">{row.label}</Text>
                {row.value && <Text as="bdi"> {row.value}</Text>}
              </CartRow>
            ))}
          </>
        )
      )}

      {bundleBaggageDetails ? (
        <>
          <BaggageHeading
            dataTestId={`${dataTestId}-baggageSection-checkedBaggageTitle`}
            icon={checkedBaggageHeadingIcon}
          >
            <span>{t('checkInBaggageHeaderTitle')}</span>
          </BaggageHeading>
          <CartRow
            className={MOUSE_FLOW_CLASS_NAMES}
            data-testid={`${dataTestId}-baggageSection-checkedBaggageRow`}
          >
            <Text as="bdi">{getBaggageDetailsTranslation(bundleBaggageDetails)}</Text>
          </CartRow>
        </>
      ) : (
        checkedBaggageSelection && (
          <>
            <BaggageHeading
              dataTestId={`${dataTestId}-baggageSection-checkedBaggageTitle`}
              icon={checkedBaggageHeadingIcon}
            >
              <span>{t('checkInBaggageHeaderTitle')}</span>
            </BaggageHeading>
            {checkedBaggageSelection?.map((row) => (
              <CartRow
                className={MOUSE_FLOW_CLASS_NAMES}
                data-testid={`${dataTestId}-baggageSection-checkedBaggageRow`}
                key={row.label}
              >
                {checkedBaggageSelection?.[0]?.noBaggageAvailable ? (
                  <Inline spacing={8}>
                    <Text data-testid="add-checked-baggage">
                      {t('Cart.CheckedBaggageInfo.NotIncluded.Text')}
                    </Text>
                    <Tooltip
                      content={t('Cart.CheckedBaggageInfo.Tooltip.Text')}
                      title={t('Cart.CheckedBaggageInfo.Overlay.Title')}
                    >
                      <InformationIconButton
                        className={infoIconTriggerStyles}
                        data-testid="add-checked-baggage-icon"
                      />
                    </Tooltip>
                  </Inline>
                ) : (
                  <Stack className={checkedBaggageCartSection} spacing={0}>
                    <Inline className={checkedBaggagedetailsPerTraveler}>
                      <Text as="bdi">{row.label}</Text>
                      {row.value && <Text as="bdi"> {row.value}</Text>}
                    </Inline>
                    {row.secondBagValue && (
                      <Inline className={secondBagdetailsPerTraveler}>
                        <Text>&#43; &nbsp;{t('secondBagTitle')}</Text>
                        <Text as="bdi"> {row.secondBagValue}</Text>
                      </Inline>
                    )}
                  </Stack>
                )}
              </CartRow>
            ))}
          </>
        )
      )}
    </>
  );
};

CartBaggageSection.propTypes = {
  bundleBaggageDetails: PropTypes.shape({
    description: PropTypes.string,
    // eslint-disable-next-line react/boolean-prop-naming
    included: PropTypes.bool,
    pieces: PropTypes.number,
    productTypeId: PropTypes.string,
    weight: PropTypes.number,
    weightUnit: PropTypes.string,
  }),
  bundleCabinBaggageDetails: PropTypes.shape({
    description: PropTypes.string,
    // eslint-disable-next-line react/boolean-prop-naming
    included: PropTypes.bool,
    pieces: PropTypes.number,
    productTypeId: PropTypes.string,
    weight: PropTypes.number,
    weightUnit: PropTypes.string,
  }),
  cabinBaggageSelection: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string,
      value: PropTypes.string,
    }),
  ),
  checkedBaggageSelection: PropTypes.oneOfType([
    PropTypes.arrayOf(
      PropTypes.shape({ label: PropTypes.string, noBaggageAvailable: PropTypes.bool }),
    ),
    PropTypes.arrayOf(
      PropTypes.shape({
        label: PropTypes.string,
        value: PropTypes.string,
      }),
    ),
  ]),

  dataTestId: PropTypes.string,
  isCabinBaggagePresent: PropTypes.bool,
  isCheckedBaggagePresent: PropTypes.bool,
  personalItem: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string,
      value: PropTypes.string,
    }),
  ),
};

export default CartBaggageSection;
